import { Button } from '@ui/themed';
import { t } from 'i18next';
import { Checklist, type ChecklistHandlers } from '../../../../components/checklist';
import { useStorageKey } from '../../../../context/use-storage-key';
import { useConfetti } from '../../../../hooks/use-confetti';
import { showIntercom } from '../../../../lib/intercom';
import type { OnboardingChecklistStep } from '../../shared/types';
import { DEFAULT_TUTORIAL_STEPS } from '../constants';

export type TutorialChecklistProps = ChecklistHandlers & {
  /** Steps to render, in order. Defaults to the full tutorial sequence. */
  steps?: OnboardingChecklistStep[];
};

// Progress header, help action, completion side effects, and the ordered steps. Steps' status hooks
// read the page providers, so this must render inside `withPageProviders`.
export function TutorialChecklist({
  steps = DEFAULT_TUTORIAL_STEPS,
  onItemComplete,
  onCompletedChange,
  onAllComplete,
}: TutorialChecklistProps) {
  const { celebrate, finale } = useConfetti();
  const storageKey = useStorageKey('tutorial');

  return (
    <Checklist
      storageKey={storageKey}
      progress={{
        title({ completed, isComplete }) {
          if (isComplete) return t('features:onboarding.pages.tutorial_page.progress_done');
          return completed > 0
            ? t('features:onboarding.pages.tutorial_page.progress_mid')
            : t('features:onboarding.pages.tutorial_page.progress_start');
        },
      }}
      actions={
        <Button variant="bare" size="sm" onClick={showIntercom}>
          {t('features:onboarding.pages.tutorial_page.get_help')}
        </Button>
      }
      onItemComplete={(event) => {
        celebrate({ anchor: event.element });
        onItemComplete?.(event);
      }}
      onCompletedChange={(isComplete) => {
        onCompletedChange?.(isComplete);
      }}
      onAllComplete={() => {
        finale();
        onAllComplete?.();
      }}
    >
      {steps.map((stepProps) => (
        <Checklist.Item key={stepProps.id} {...stepProps} />
      ))}
    </Checklist>
  );
}
